// Time utility functions for generating slots and converting timezones

import { TimeSlot } from '../types';

/**
 * Generates 48 time slots for a full day (00:00 to 23:30)
 * Each slot is 30 minutes apart
 * @returns Array of TimeSlot objects in local timezone
 */
export function generateTimeSlots(): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const baseDate = new Date();
  baseDate.setHours(0, 0, 0, 0);

  for (let i = 0; i < 48; i++) {
    const time = new Date(baseDate);
    const totalMinutes = i * 30;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    
    time.setHours(hour, minute, 0, 0);

    slots.push({
      time,
      hour,
      minute,
      label: formatTimeLabel(hour, minute)
    });
  }

  return slots;
}

/**
 * Formats time as HH:MM string
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @returns Formatted time string like "14:00" or "09:30"
 */
export function formatTimeLabel(hour: number, minute: number): string {
  const hourStr = hour.toString().padStart(2, '0');
  const minuteStr = minute.toString().padStart(2, '0');
  return `${hourStr}:${minuteStr}`;
}

/**
 * Converts local time to GMT ISO string
 * @param localTime - Date object in local timezone
 * @returns ISO string in GMT timezone
 */
export function localToGMT(localTime: Date): string {
  return localTime.toISOString();
}

/**
 * Converts GMT ISO string to local Date object
 * @param gmtString - ISO string in GMT timezone
 * @returns Date object in local timezone
 */
export function gmtToLocal(gmtString: string): Date {
  return new Date(gmtString);
}

/**
 * Formats a Date object to display time in local timezone
 * @param date - Date object
 * @returns Formatted time string like "14:00"
 */
export function formatTime(date: Date): string {
  return formatTimeLabel(date.getHours(), date.getMinutes());
}

/**
 * Creates a Date object for a specific time slot on today's date
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @returns Date object with specified time
 */
export function createTimeSlotDate(hour: number, minute: number): Date {
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date;
}

/**
 * Checks if two time slots represent the same time
 * @param slot1 - First time slot (ISO string or Date)
 * @param slot2 - Second time slot (ISO string or Date)
 * @returns True if times match
 */
export function isSameTimeSlot(slot1: string | Date, slot2: string | Date): boolean {
  const date1 = typeof slot1 === 'string' ? new Date(slot1) : slot1;
  const date2 = typeof slot2 === 'string' ? new Date(slot2) : slot2;
  
  return date1.getTime() === date2.getTime();
}
